import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Globe, Monitor, Smartphone, Tablet } from "lucide-react";
import { Panel } from "@/components/admin/Fields";
import { BarRow, COLOURS, Empty, Figure, nb, pct } from "@/components/admin/stats/StatsBits";
import { cn } from "@/lib/utils";

const DAY = ["Må", "Ty", "On", "To", "Fr", "La", "Su"];
const DAY_LONG = ["måndag", "tysdag", "onsdag", "torsdag", "fredag", "laurdag", "sundag"];

/** Most read pages, with the share of all views. */
export const TopPages = ({ pages = [], landing = [] }) => (
  <Panel title="Mest lesne sider" description="Sidevisingar i perioden" testId="stats-pages">
    <div className="px-4 pb-4 sm:px-5">
      {!pages.length ? <Empty text="Ingen sidevisingar enno" /> : pages.slice(0, 10).map((p, i) => <BarRow key={p.path} label={p.title} sub={p.path !== "/" ? p.path : undefined} value={p.views} max={pages[0].views} display={`${nb(p.views)} · ${pct(p.share)}`} colour={i === 0 ? COLOURS[0] : "hsl(var(--foreground) / 0.45)"} testId={`stats-page-${i}`} />)}
      {landing.length > 0 && (
        <div className="mt-5 border-t border-border pt-4">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Første side folk landar på</p>
          <ul className="mt-2 flex flex-wrap gap-1.5" data-testid="stats-landing">{landing.map((l) => <li key={l.path} className="rounded-full bg-secondary px-2.5 py-1 text-[12px]">{l.title} <span className="text-muted-foreground tabular">{l.sessions}</span></li>)}</ul>
        </div>
      )}
    </div>
  </Panel>
);

/** Where sessions came from – donut + the actual referring sites. */
export const SourcesPanel = ({ sources = [], referrers = [] }) => {
  const data = sources.filter((s) => s.sessions > 0);
  const total = data.reduce((a, s) => a + s.sessions, 0);
  return (
    <Panel title="Kvar kjem folk frå?" description="Kjelda til kvar økt (første sidevising)" testId="stats-sources">
      <div className="grid gap-4 px-4 pb-4 sm:grid-cols-[150px_minmax(0,1fr)] sm:px-5">
        <div className="relative mx-auto h-[150px] w-[150px]">
          {total ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="sessions" nameKey="label" innerRadius={50} outerRadius={70} paddingAngle={2} stroke="none" animationDuration={700}>{data.map((s, i) => <Cell key={s.key} fill={COLOURS[i % COLOURS.length]} />)}</Pie>
                <Tooltip formatter={(v, n) => [`${nb(v)} økter`, n]} contentStyle={{ borderRadius: 12, fontSize: 12.5, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="flex h-full items-center justify-center rounded-full border border-dashed border-border text-muted-foreground"><Globe className="h-5 w-5" /></div>}
          {total > 0 && <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"><span className="font-display text-[22px] font-semibold leading-none tabular">{nb(total)}</span><span className="mt-1 text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground">økter</span></div>}
        </div>
        <div className="min-w-0">
          <ul className="space-y-2" data-testid="stats-sources-list">
            {sources.map((s, i) => <li key={s.key} className="flex items-center justify-between gap-3 text-[13.5px]"><span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: s.sessions ? COLOURS[data.findIndex((d) => d.key === s.key) % COLOURS.length] : "hsl(var(--border))" }} />{s.label}</span><span className="tabular"><span className="font-medium">{nb(s.sessions)}</span> <span className="text-muted-foreground">{pct(s.share)}</span></span></li>)}
          </ul>
          {referrers.length > 0 && (
            <div className="mt-4 border-t border-border pt-3">
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Nettstader som sender folk hit</p>
              <ul className="mt-1.5 space-y-1" data-testid="stats-referrers">{referrers.map((r) => <li key={r.host} className="flex items-center justify-between text-[13px]"><a href={`https://${r.host}`} target="_blank" rel="noopener noreferrer" className="truncate u-link">{r.host}</a><span className="tabular text-muted-foreground">{nb(r.sessions)}</span></li>)}</ul>
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
};

const ICON = { mobile: Smartphone, tablet: Tablet, desktop: Monitor };

/** Mobile / tablet / PC as one stacked bar with the three shares below. */
export const DevicesPanel = ({ devices = [] }) => {
  const total = devices.reduce((a, d) => a + d.views, 0);
  return (
    <Panel title="Mobil eller PC?" description="Skjermstorleik ved kvar sidevising" testId="stats-devices">
      <div className="px-4 pb-4 sm:px-5">
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-foreground/[0.06]" aria-hidden="true">{devices.map((d, i) => d.views > 0 && <div key={d.key} style={{ width: `${(d.views / total) * 100}%`, background: COLOURS[i] }} className="h-full transition-[width] duration-700" />)}</div>
        <div className="mt-4 grid grid-cols-3 gap-3">
          {devices.map((d, i) => { const Icon = ICON[d.key] || Monitor; return <Figure key={d.key} label={<span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: COLOURS[i] }} /><Icon className="h-3 w-3" />{d.label}</span>} value={total ? pct(d.share) : "–"} hint={`${nb(d.views)} visingar`} testId={`stats-device-${d.key}`} />; })}
        </div>
      </div>
    </Panel>
  );
};

/** When during the day and week the site is used – 24 small bars and 7 weekday cells. */
export const HoursPanel = ({ hours = [], weekdays = [] }) => {
  const maxH = Math.max(1, ...hours), maxD = Math.max(1, ...weekdays);
  const peak = hours.indexOf(Math.max(...hours));
  const total = hours.reduce((a, b) => a + b, 0);
  const hh = (h) => String(h % 24).padStart(2, "0");
  const desc = total ? `Flest besøk kl. ${hh(peak)}–${hh(peak + 1)} · travlast ${DAY_LONG[weekdays.indexOf(Math.max(...weekdays))]}` : "Timar og vekedagar (norsk tid)";
  return (
    <Panel title="Når er folk innom?" description={desc} testId="stats-hours">
      <div className="px-4 pb-4 sm:px-5">
        <div className="flex h-[88px] items-end gap-[3px]" role="img" aria-label="Sidevisingar per time">
          {hours.map((h, i) => <div key={i} className="group relative flex-1" title={`kl. ${String(i).padStart(2, "0")}: ${nb(h)}`}><div className={cn("w-full rounded-t-[3px] transition-[height] duration-700", i === peak && total ? "bg-sol" : "bg-foreground/25")} style={{ height: `${Math.max(total ? 3 : 2, (h / maxH) * 88)}px` }} /></div>)}
        </div>
        <div className="mt-1.5 flex justify-between text-[10.5px] text-muted-foreground tabular"><span>00</span><span>06</span><span>12</span><span>18</span><span>23</span></div>
        <div className="mt-5 grid grid-cols-7 gap-1.5" data-testid="stats-weekdays">
          {weekdays.map((d, i) => <div key={i} className="rounded-[8px] px-1 py-2 text-center" style={{ background: `hsl(var(--sol) / ${total ? 0.08 + (d / maxD) * 0.72 : 0.06})` }} title={`${nb(d)} visingar`}><p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-foreground/70">{DAY[i]}</p><p className="mt-0.5 text-[12.5px] font-medium tabular">{nb(d)}</p></div>)}
        </div>
      </div>
    </Panel>
  );
};
